import { useEffect, useRef, useState } from "react";
import { Plus, X, Eye, EyeOff, ChevronDown } from "lucide-react";
import { AutoTextarea } from "./AutoTextarea";

const ListEditor = ({ items, onChange, placeholder, testid, chip }) => {
  const [draft, setDraft] = useState("");
  const [focusIdx, setFocusIdx] = useState(null);
  const refs = useRef([]);
  const add = () => { if (draft.trim()) { onChange([...items, draft.trim()]); setDraft(""); } };
  const insertAfter = (i) => { onChange([...items.slice(0, i + 1), "", ...items.slice(i + 1)]); setFocusIdx(i + 1); };  // Enter in a task = new task below
  useEffect(() => { if (focusIdx != null) { refs.current[focusIdx]?.focus(); setFocusIdx(null); } }, [focusIdx, items]);
  return (
    <div data-testid={testid}>
      <div className={chip ? "flex flex-wrap gap-2" : "space-y-2"}>
        {items.map((t, i) => (
          <div key={i} className={chip ? "chip chip-yellow flex items-center gap-1" : "task-row"} data-testid={`${testid}-item-${i}`}>
            {chip ? <span>⚠ {t}</span> : <AutoTextarea className="field-sm flex-1" value={t} onRef={(el) => { refs.current[i] = el; }} onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} onEnter={() => insertAfter(i)} data-testid={`${testid}-input-${i}`} />}
            <button type="button" className="x-btn" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`} aria-label="Remove"><X size={14} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <AutoTextarea className="field-sm flex-1" placeholder={placeholder} value={draft} onChange={(e) => setDraft(e.target.value)} onEnter={add} data-testid={`${testid}-new-input`} />
        <button type="button" className="icon-btn" onClick={add} data-testid={`${testid}-add-button`} aria-label="Add"><Plus size={16} /></button>
      </div>
    </div>
  );
};

export const JobCardEditor = ({ card, onChange }) => {
  const [showCode, setShowCode] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const set = (k, v) => onChange({ ...card, [k]: v });
  return (
    <div className="space-y-4" data-testid="job-card-editor">
      <div>
        <label className="eyebrow">Job title</label>
        <AutoTextarea className="mt-1 font-bold" value={card.title} onChange={(e) => set("title", e.target.value.replace(/\n/g, " "))} onEnter={(e) => e.target.blur()} data-testid="job-title-input" placeholder="Job title" />
      </div>
      <div>
        <label className="eyebrow">Tasks</label>
        <div className="mt-1"><ListEditor items={card.tasks} onChange={(v) => set("tasks", v)} placeholder="Add a task" testid="job-tasks" /></div>
      </div>
      <div>
        <label className="eyebrow">Hazards</label>
        <div className="mt-1"><ListEditor items={card.hazards} onChange={(v) => set("hazards", v)} placeholder="Add a hazard" testid="job-hazards" chip /></div>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <div className="relative">
          <label className="eyebrow">Gate / access code</label>
          <input className="field mt-1 pr-12" type={showCode ? "text" : "password"} value={card.access_code} onChange={(e) => set("access_code", e.target.value)} data-testid="job-access-code-input" placeholder="Optional" autoComplete="off" />
          <button type="button" className="reveal-btn" onClick={() => setShowCode(!showCode)} data-testid="job-access-code-reveal" aria-label="Reveal code">{showCode ? <EyeOff size={16} /> : <Eye size={16} />}</button>
        </div>
        <div>
          <label className="eyebrow">Access notes</label>
          <AutoTextarea className="mt-1" value={card.access_notes} onChange={(e) => set("access_notes", e.target.value)} onEnter={(e) => e.target.blur()} data-testid="job-access-notes-input" placeholder="e.g. key under the pot, dog in yard" />
        </div>
      </div>
      {card.transcript && (
        <div>
          <button type="button" className="flex items-center gap-1 text-sm text-muted" onClick={() => setShowTranscript(!showTranscript)} data-testid="job-transcript-toggle">
            <ChevronDown size={14} className={showTranscript ? "rotate-180" : ""} /> Transcript {card.language ? `(${card.language})` : ""}
          </button>
          {showTranscript && <p className="text-sm text-muted mt-2 result" data-testid="job-transcript">“{card.transcript}”</p>}
        </div>
      )}
    </div>
  );
};
